import { useState } from "react";
import { marketing } from "@/site/api";
import { attribution, track } from "@/site/analytics";
import { SITE } from "@/site/content";

const FREE = /@(gmail|googlemail|yahoo|hotmail|outlook|live|icloud|me|aol|proton|protonmail|mail|gmx|yandex)\./i;

/** Real form: posts to /api/marketing/leads. Honeypot, work-email check, UTM attached. */
export default function LeadForm({ intent = "pilot", compact = false, source = "form", showTabs = true }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", ams: "5-9", crm: "salesforce", message: "", preferred_time: "", website: "" });
  const [mode, setMode] = useState(intent);
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [serverErr, setServerErr] = useState("");
  const set = (k) => (e) => {
    const v = e.target.value;
    setF((s) => ({ ...s, [k]: v }));
    setErrs((s) => (s[k] ? { ...s, [k]: undefined } : s));
  };

  const validate = () => {
    const e = {};
    if (f.name.trim().length < 2) e.name = "Your name, please.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(f.email)) e.email = "A valid email address.";
    else if (FREE.test(f.email)) e.email = "Please use your work email.";
    if (f.company.trim().length < 2) e.company = "Company name.";
    if (mode === "contact" && f.message.trim().length < 5) e.message = "What's your question?";
    setErrs(e); return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setBusy(true); setServerErr("");
    try {
      // The contact form doesn't ask for team size or CRM, so don't send the pilot form's defaults.
      const { ams, crm, ...rest } = f;
      const body = mode === "contact" ? rest : f;
      await marketing.lead({ ...body, intent: mode, utm: attribution(), page: window.location.pathname });
      track("lead_submitted", mode === "contact" ? { intent: mode, source } : { intent: mode, ams, crm, source });
      setDone(true);
    } catch (e) {
      setServerErr(e?.response?.data?.detail || (SITE.email ? "Something went wrong. Email us at " + SITE.email : "Something went wrong. Please try again in a moment."));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="lead lead__ok" data-testid="lead-success" role="status">
        <div className="eyebrow">Received</div>
        <h3 className="h3">Thanks, {f.name.split(" ")[0]}. We'll reply within one working day.</h3>
        {mode !== "contact" && (
          <p className="body">
            {mode === "walkthrough"
              ? (SITE.bookingUrl ? <>One last step: <a href={SITE.bookingUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("booking_link_click", { source })} data-testid="lead-booking-link">pick a time for your 30-minute walkthrough →</a></> : "We'll propose two slots for a 30-minute walkthrough.")
              : "We'll send a CSV template so you can price your real book in the first session."}
            {SITE.email && <> Prefer email? <a href={`mailto:${SITE.email}`}>{SITE.email}</a></>}
          </p>
        )}
      </div>
    );
  }

  return (
    <form className="lead" onSubmit={submit} noValidate data-testid="lead-form">
      {showTabs && (
        <div style={{ display: "flex", gap: 8 }} role="tablist" aria-label="What would you like?">
          <button type="button" role="tab" aria-selected={mode === "pilot"} className={`btn btn--sm ${mode === "pilot" ? "btn--ink" : "btn--ghost"}`} onClick={() => setMode("pilot")} data-testid="lead-mode-pilot">Start a pilot</button>
          <button type="button" role="tab" aria-selected={mode === "walkthrough"} className={`btn btn--sm ${mode === "walkthrough" ? "btn--ink" : "btn--ghost"}`} onClick={() => setMode("walkthrough")} data-testid="lead-mode-walkthrough">Book a 30-min walkthrough</button>
        </div>
      )}
      <div className="lead__row" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
        <div className="field"><label htmlFor="lf-name">Name</label><input id="lf-name" className="input" value={f.name} onChange={set("name")} autoComplete="name" data-testid="lead-name" />{errs.name && <span className="err">{errs.name}</span>}</div>
        <div className="field"><label htmlFor="lf-email">Work email</label><input id="lf-email" className="input" type="email" value={f.email} onChange={set("email")} autoComplete="email" data-testid="lead-email" />{errs.email && <span className="err" data-testid="lead-email-error">{errs.email}</span>}</div>
        <div className="field"><label htmlFor="lf-phone">Phone (optional)</label><input id="lf-phone" className="input" type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" data-testid="lead-phone" /></div>
      </div>
      {mode === "contact" ? (
        <>
          <div className="lead__row" style={{ gridTemplateColumns: "1fr" }}>
            <div className="field"><label htmlFor="lf-company">Company</label><input id="lf-company" className="input" value={f.company} onChange={set("company")} autoComplete="organization" data-testid="lead-company" />{errs.company && <span className="err">{errs.company}</span>}</div>
          </div>
          <div className="lead__row" style={{ gridTemplateColumns: "1fr" }}>
            <div className="field"><label htmlFor="lf-msg">What's your question?</label><textarea id="lf-msg" className="input" rows={4} placeholder="e.g. a question about how you handle our data, or something about the terms of use" value={f.message} onChange={set("message")} data-testid="lead-message" />{errs.message && <span className="err">{errs.message}</span>}</div>
          </div>
        </>
      ) : (
        <>
          <div className="lead__row">
            <div className="field"><label htmlFor="lf-company">Company</label><input id="lf-company" className="input" value={f.company} onChange={set("company")} autoComplete="organization" data-testid="lead-company" />{errs.company && <span className="err">{errs.company}</span>}</div>
            <div className="field"><label htmlFor="lf-ams">Account managers</label>
              <select id="lf-ams" className="select" value={f.ams} onChange={set("ams")} data-testid="lead-ams"><option value="1-4">1–4</option><option value="5-9">5–9</option><option value="10-20">10–20</option><option value="20+">20+</option></select></div>
          </div>
          <div className="lead__row">
            <div className="field"><label htmlFor="lf-crm">CRM</label>
              <select id="lf-crm" className="select" value={f.crm} onChange={set("crm")} data-testid="lead-crm"><option value="salesforce">Salesforce</option><option value="hubspot">HubSpot</option><option value="other">Other</option><option value="none">None yet</option></select></div>
            <div className="field"><label htmlFor="lf-msg">Anything we should know? (optional)</label><input id="lf-msg" className="input" placeholder="e.g. we sell in EUR and GBP" value={f.message} onChange={set("message")} /></div>
          </div>
        </>
      )}
      <label className="hp" aria-hidden="true">Website<input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} /></label>
      {serverErr && <div className="notice" role="alert" data-testid="lead-server-error">{serverErr}</div>}
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <button className="btn btn--primary btn--lg" type="submit" disabled={busy} data-testid="lead-submit">{busy ? "Sending…" : mode === "pilot" ? "Start a pilot →" : mode === "walkthrough" ? "Book a walkthrough →" : "Send message →"}</button>
        <span className="hint" style={{ fontSize: 12.5, color: "var(--faint)" }}>No marketing list. We reply personally. <a href="/privacy" style={{ color: "inherit" }}>Privacy</a></span>
      </div>
    </form>
  );
}
